import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getActiveEntitySlug, getCurrentBrand } from '../utils/activeEntity';
import './BrandGettingStarted.css';

/**
 * Getting started (Overview, Brand Admins): the few steps that take a new brand
 * from empty to live for its members. Each step links to where it's done, and
 * the card disappears once the brand is set up (or is dismissed).
 */

const DISMISS_KEY = 'risklo.gettingStarted.dismissed';

function readDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); } catch (_e) { return []; }
}

export default function BrandGettingStarted({ onNavigate }) {
  const slug = getActiveEntitySlug() || 'vector';
  const [strategies, setStrategies] = useState(null);
  const [dismissed, setDismissed] = useState(() => readDismissed().includes(slug));

  useEffect(() => {
    if (dismissed) return undefined;
    let live = true;
    fetch(API_ENDPOINTS.adminBrandStrategies, { headers: getBearerAuthHeaders() })
      .then((r) => r.json())
      .then((data) => { if (live && data.success) setStrategies(data.strategies || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [dismissed, slug]);

  if (dismissed || !strategies) return null;

  const brand = getCurrentBrand();
  const steps = [
    {
      id: 'add',
      label: 'Add your first strategy',
      done: strategies.length > 0,
      go: 'brand-strategies',
    },
    {
      id: 'data',
      label: 'Upload past results or connect live data',
      done: strategies.some((s) => s.liveStatus === 'live' || s.liveStatus === 'none'),
      go: 'brand-strategies',
    },
    {
      id: 'visible',
      label: 'Make a strategy visible to your members',
      done: strategies.some((s) => s.visible),
      go: 'brand-strategies',
    },
    {
      id: 'logo',
      label: 'Add your logo',
      done: Boolean(brand?.branding?.logoUrl),
      go: 'brand-settings',
    },
    {
      id: 'team',
      label: 'Invite your team',
      done: false,
      optional: true,
      go: 'admin-users',
    },
  ];
  const required = steps.filter((s) => !s.optional);
  const doneCount = required.filter((s) => s.done).length;
  if (doneCount === required.length) return null; // set up: nothing to show

  const dismiss = () => {
    const next = [...new Set([...readDismissed(), slug])];
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch (_e) { /* private mode */ }
    setDismissed(true);
  };

  return (
    <section className="bgs" aria-label="Getting started">
      <div className="bgs-head">
        <div>
          <strong>Get your brand ready</strong>
          <span className="bgs-progress">{doneCount} of {required.length} done</span>
        </div>
        <button type="button" className="bgs-dismiss" onClick={dismiss}>Hide</button>
      </div>
      <div className="bgs-bar" aria-hidden="true"><span style={{ width: `${(doneCount / required.length) * 100}%` }} /></div>
      <ol className="bgs-steps">
        {steps.map((s) => (
          <li key={s.id} className={s.done ? 'is-done' : ''}>
            <span className="bgs-check" aria-hidden="true">{s.done ? '✓' : ''}</span>
            <span className="bgs-label">{s.label}{s.optional ? <em> (optional)</em> : null}</span>
            {!s.done ? (
              <button type="button" className="bgs-go" onClick={() => onNavigate && onNavigate(s.go)}>Go</button>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}
